import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import SiteNav from "../components/SiteNav";
import SiteFooter from "../components/SiteFooter";
import Brand from "../components/Brand";
import {
  ArrowLeft, ExternalLink, ShieldCheck, Truck, RefreshCw,
  Award, Droplet, Wind, Cpu, Battery, Play, Sparkles, ArrowRight, Star,
} from "lucide-react";

/* /piurify — dedicated affiliate landing page for the Piurify hydrogen
   water bottle. Mike earns 10% on every sale via the sca_ref link, and
   anyone who wants to sell it themselves can sign up at uppromote. */

const G = "#2E9F3A";
const O = "#F39A1F";
const WINE = "#0F172A";
const DARK = "#0F172A";

const AFFILIATE_URL = "https://www.piurify.com?sca_ref=11278377.5KM2AvgiFX";
const PARTNER_SIGNUP_URL = "https://marketplace.uppromote.com/register";

const BOTTLE_ROSE   = "https://customer-assets.emergentagent.com/job_grace-community-plus/artifacts/jzop1227_Screenshot%202026-05-13%20134733.png";
const BOTTLE_TEAL   = "https://customer-assets.emergentagent.com/job_grace-community-plus/artifacts/c8f6uix8_Screenshot%202026-05-13%20134456.png";
const BOTTLE_SILVER = "https://customer-assets.emergentagent.com/job_grace-community-plus/artifacts/ujmihc6m_Screenshot%202026-05-13%20134444.png";
const BOTTLE_BLACK  = "https://customer-assets.emergentagent.com/job_grace-community-plus/artifacts/38b8auz7_Screenshot%202026-05-13%20132626.png";
const BOTTLE_ROSE_HERO = "https://customer-assets.emergentagent.com/job_grace-community-plus/artifacts/xg6blyqf_Screenshot%202026-05-13%20134456.png";
const FEATURE_GRID  = "https://customer-assets.emergentagent.com/job_grace-community-plus/artifacts/dg1nootv_Screenshot%202026-05-13%20134434.png";
const BENEFITS_GRAPHIC = "https://customer-assets.emergentagent.com/job_grace-community-plus/artifacts/kuc3wdjy_Screenshot%202026-05-13%20134124.png";
const HOWTO_GRAPHIC = "https://customer-assets.emergentagent.com/job_grace-community-plus/artifacts/2p569ycn_Screenshot%202026-05-13%20134043.png";
const VIDEO_MP4    = "https://customer-assets.emergentagent.com/job_grace-community-plus/artifacts/w99f59al_96e3923ad0f14eb8bb771d6afc4c0b9b.HD-1080p-3.3Mbps-82931261.mp4";
const VIDEO_POSTER = "https://customer-assets.emergentagent.com/job_grace-community-plus/artifacts/5e4dmb78_Screenshot%202026-05-13%20134341.png";
const VIDEO_THUMB   = "https://customer-assets.emergentagent.com/job_grace-community-plus/artifacts/5e4dmb78_Screenshot%202026-05-13%20134341.png";
const SWATCH_GRAPHIC= "https://customer-assets.emergentagent.com/job_grace-community-plus/artifacts/tsjutano_Screenshot%202026-05-13%20134512.png";
const FAMILY_LINEUP = "/products/piurify-echo-flask.png";
const UPGRADE_HERO  = "https://customer-assets.emergentagent.com/job_grace-community-plus/artifacts/ptct665m_Screenshot%202026-05-13%20142954.png";

/* ─── Hydrogenator® Flask (the bigger-brother product) ─── */
const TABLETOP_BLUE  = "https://customer-assets.emergentagent.com/job_grace-community-plus/artifacts/0i3v2dce_Screenshot%202026-05-13%20214457.png";
const TABLETOP_BLACK = "https://customer-assets.emergentagent.com/job_grace-community-plus/artifacts/3pgfdh52_Screenshot%202026-05-13%20214433.png";
const PPB_GRAPHIC    = "https://customer-assets.emergentagent.com/job_grace-community-plus/artifacts/88qcmhs8_Screenshot%202026-05-13%20214838.png";
const STUDIES_GRAPHIC= "https://customer-assets.emergentagent.com/job_grace-community-plus/artifacts/6frfwv5k_Screenshot%202026-05-13%20214719.png";
/* Flask walk-through video — Mike's second clip */
const FLASK_VIDEO_MP4 = "https://customer-assets.emergentagent.com/job_grace-community-plus/artifacts/lwhmexjc_2395dea29a62403882acfcdb917e54da.HD-1080p-7.2Mbps-82594026.mp4";

const TABLETOP_COLORS = [
  { key: "blue",  label: "Blue",  hex: "#4F77B5", img: TABLETOP_BLUE  },
  { key: "black", label: "Black", hex: "#1A1A1C", img: TABLETOP_BLACK },
];

/* Interactive colour picker — clicking a swatch swaps the hero bottle photo.
   We now have all 4 colour photos: Pink, Silver, Teal, Black. */
const COLORS = [
  { key: "pink",   label: "Pink",   hex: "#E9C2CC", img: BOTTLE_ROSE },
  { key: "silver", label: "Silver", hex: "#C9CCD1", img: BOTTLE_SILVER },
  { key: "teal",   label: "Teal",   hex: "#1FA9B9", img: BOTTLE_TEAL },
  { key: "black",  label: "Black",  hex: "#1A1A1C", img: BOTTLE_BLACK },
];

/* "Advanced Water Solutions for Optimal Health" — the real Piurify line-up
   straight from their store with actual review counts + on-sale pricing.
   Every CTA goes through the affiliate URL so Mike still earns 10% on
   whichever product the visitor ends up buying. */
const FAVORITES = [
  {
    key: "hydrogenator-pitcher",
    badge: "MOST POPULAR",
    badgeColor: "#287F8E",
    stars: 4.5,
    reviews: 504,
    name: "Piurify Water Hydrogenator® Pitcher",
    desc: "Deep hydration with long-lasting hydrogen benefits.",
    price: "$ —",
    wasPrice: "$ —",
    sale: true,
  },
  {
    key: "hydrogenator-bottle-black",
    badge: "BESTSELLER",
    badgeColor: G,
    stars: 4.5,
    reviews: 423,
    name: "Hydrogenator® Hydrogen Water Bottle — Black",
    desc: "Portable, high-performance hydration.",
    price: "$ —",
    wasPrice: "$ —",
    sale: true,
  },
  {
    key: "hydrogenator-handle",
    badge: "NEW ARRIVAL",
    badgeColor: O,
    stars: 5.0,
    reviews: 5,
    name: "Hydrogenator® Bottle With Handle — Dark Blue",
    desc: "17 oz capacity for a full day of hydration.",
    price: "$ —",
    wasPrice: "$ —",
    sale: true,
  },
  {
    key: "hydrogenator-flask",
    badge: "SOLD OUT",
    badgeColor: WINE,
    stars: 5.0,
    reviews: 1,
    name: "Hydrogenator® Flask — Black",
    desc: "Our most powerful hydrogen experience.",
    price: "$ —",
    wasPrice: "$ —",
    sale: true,
    soldOut: true,
  },
];

const FEATURES = [
  {
    Icon: Battery,
    title: "Two Smart Hydrogenation Modes",
    body: "5-minute mode (2500 ppb) for quick hydration. 10-minute mode (over 4000 ppb) for deep detox & recovery. You decide what kind of day you're having.",
    color: G,
  },
  {
    Icon: Wind,
    title: "Nasal Cannula Included",
    body: "Drink AND breathe the hydrogen for maximum absorption. Dual-delivery is what separates a real H2 bottle from a gimmick.",
    color: O,
  },
  {
    Icon: Cpu,
    title: "Smart OLED Display",
    body: "Track hydrogen levels and battery life so you know every session is doing what it's supposed to. No guessing.",
    color: WINE,
  },
  {
    Icon: Droplet,
    title: "DuPont SPE/PEM Technology",
    body: "Industry-leading 99.99% purity, over 6,200 PPB concentration. The same membrane technology hospitals use for medical-grade hydrogen.",
    color: "#287F8E",
  },
];

const BADGES = [
  { Icon: Award,       label: "H2 Certified",      sub: "Independently tested to IHSA standards" },
  { Icon: ShieldCheck, label: "Lifetime Warranty", sub: "Forever — not 30 days, not a year" },
  { Icon: RefreshCw,   label: "60-Day Returns",    sub: "Try it. Don't love it? Send it back." },
  { Icon: Truck,       label: "Free Shipping",     sub: "Direct from Piurify, on the house" },
];

export default function PiurifyPage() {
  const nav = useNavigate();
  const loc = useLocation();
  const [color, setColor] = useState(COLORS[0]); // bottle colour
  const [tableColor, setTableColor] = useState(TABLETOP_COLORS[0]); // tabletop colour

  const goBack = () => {
    if (window.history.length > 1 && loc.key !== "default") nav(-1);
    else nav("/market");
  };

  const buy = (where) => () => {
    // open in new tab; the sca_ref in the URL is what credits Mike's 10%
    window.open(AFFILIATE_URL, "_blank", "noopener,noreferrer");
    try {
      // eslint-disable-next-line no-console
      console.log("[piurify] outbound click from", where);
    } catch (_e) { /* no-op */ }
  };

  const pickColor = (c) => {
    setColor(c);
  };

  return (
    <div className="min-h-screen" style={{ background: "#FAFCFE" }} data-testid="piurify-page">
      <SiteNav />

      {/* Floating back */}
      <button onClick={goBack} data-testid="piurify-back"
              className="fixed top-20 left-3 md:left-6 z-30 inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-xs uppercase tracking-[0.22em] font-bold shadow-md hover:scale-[1.04] transition"
              style={{ background: DARK, color: O, border: `1.5px solid ${O}` }}>
        <ArrowLeft className="w-4 h-4" /> Back
      </button>

      {/* ═══ HERO ═══ */}
      <section className="relative overflow-hidden"
               style={{ background: "linear-gradient(160deg, #0F1A26 0%, #182E4A 60%, #1F4068 100%)" }}>
        <div className="absolute inset-0 pointer-events-none opacity-30"
             style={{ backgroundImage: "radial-gradient(circle at 20% 30%, rgba(64,176,255,0.4), transparent 45%), radial-gradient(circle at 85% 80%, rgba(243,154,31,0.25), transparent 50%)" }}/>
        <div className="relative max-w-6xl mx-auto px-5 md:px-8 py-12 md:py-20 grid md:grid-cols-[1fr_360px] gap-10 items-center text-white">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[10px] tracking-[0.28em] uppercase font-bold mb-4"
                 style={{ background: O, color: DARK }}>
              <Sparkles className="w-3.5 h-3.5" /> A <Brand light /> WELLNESS PARTNER
            </div>
            <h1 className="font-serif-display text-4xl sm:text-5xl md:text-6xl leading-[1.04] uppercase"
                style={{ textWrap: "balance", color: "#E8F4FF" }}>
              Hydrogen Water<br/>That Actually Works.
            </h1>
            <p className="text-base md:text-xl italic mt-5 max-w-xl text-white/85" style={{ textWrap: "pretty" }}>
              The <strong className="not-italic text-[#F39A1F]">Piurify Hydrogen Bottle</strong> isn't a gadget — it's a portable hydrogen generator that turns ordinary water into the same molecular hydrogen athletes and clinics pay big money for. <strong className="not-italic">Trusted and tested.</strong>
            </p>
            <div className="mt-6 flex flex-wrap items-baseline gap-3">
              <div className="font-serif-display text-5xl md:text-6xl font-bold" style={{ color: "#F39A1F" }}>$ —</div>
              <div className="text-[11px] tracking-[0.18em] uppercase font-bold text-white/70">Direct from Piurify · Free shipping</div>
            </div>
            <div className="mt-7 flex flex-wrap items-center gap-3">
              <button onClick={buy("hero")} data-testid="piurify-buy-hero"
                      className="inline-flex items-center gap-2 px-6 py-3.5 rounded-md text-sm font-bold uppercase tracking-[0.18em] text-white shadow-lg hover:scale-[1.03] transition"
                      style={{ background: G }}>
                Get Your Piurify Bottle <ExternalLink className="w-4 h-4" />
              </button>
              <a href="#features" className="inline-flex items-center gap-2 px-6 py-3.5 rounded-md text-sm font-bold uppercase tracking-[0.18em]"
                 style={{ background: "#FFFFFF22", color: "#FFFFFF", border: "1.5px solid #F39A1F" }}
                 data-testid="piurify-learn-more">
                See How It Works <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </div>

          {/* Hero bottle — swaps based on the selected swatch below */}
          <div className="mx-auto" data-testid="piurify-hero-bottle">
            <div className="rounded-3xl bg-white p-5 shadow-2xl transition-all" style={{ border: `4px solid ${O}` }}>
              <img
                src={color.img || BOTTLE_ROSE}
                alt={`Piurify Hydrogen Bottle — ${color.label}`}
                className="block w-full h-auto transition-opacity duration-200"
                style={{ maxWidth: 320 }}
                key={color.key}
                data-testid="piurify-hero-bottle-img"
              />
            </div>

            {/* Interactive colour picker — Pink · Silver · Teal · Black */}
            <div className="mt-4" data-testid="piurify-color-picker">
              <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-center text-white/75 mb-2">
                Color: <span style={{ color: O }}>{color.label}</span>
              </div>
              <div className="flex items-center justify-center gap-3">
                {COLORS.map((c) => {
                  const active = c.key === color.key;
                  return (
                    <button
                      key={c.key}
                      type="button"
                      onClick={() => pickColor(c)}
                      data-testid={`piurify-swatch-${c.key}`}
                      title={c.label}
                      aria-label={`Choose ${c.label}`}
                      aria-pressed={active}
                      className="w-10 h-12 rounded-md transition-all hover:scale-110 focus:outline-none"
                      style={{
                        background: c.hex,
                        border: active ? `3px solid #FFF` : `2px solid rgba(255,255,255,0.35)`,
                        boxShadow: active ? `0 0 0 3px ${O}, 0 4px 14px rgba(0,0,0,0.4)` : `0 2px 6px rgba(0,0,0,0.25)`,
                      }}
                    />
                  );
                })}
              </div>
              <div className="mt-2 text-center text-[11px] italic text-white/60">
                Tap a colour to preview · All 4 ship from Piurify
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ TRUST BADGES ═══ */}
      <section className="max-w-6xl mx-auto px-5 md:px-8 -mt-7 relative z-20">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 bg-white rounded-2xl shadow-2xl border-2 p-4 md:p-5"
             style={{ borderColor: `${O}55` }} data-testid="piurify-badges">
          {BADGES.map((b) => (
            <div key={b.label} className="flex flex-col items-center text-center px-2 py-2">
              <b.Icon className="w-7 h-7 mb-2" style={{ color: WINE }} />
              <div className="text-[11px] tracking-[0.18em] uppercase font-bold" style={{ color: DARK }}>{b.label}</div>
              <div className="text-[11px] text-foreground/65 leading-tight mt-1">{b.sub}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ═══ WHAT THIS IS · WHY MIKE PICKED IT — senior-friendly orientation ═══
          Drops the customer into plain English before any tech jargon.
          Mike's actual voice. No DuPont SPE/PEM until they want to read it. */}
      <section className="max-w-5xl mx-auto px-5 md:px-8 mt-10" data-testid="piurify-mike-intro">
        <div className="rounded-2xl p-6 md:p-8 shadow-md grid md:grid-cols-[1fr_220px] gap-6 items-center"
             style={{ background: "#EAF6FC", border: `2px solid ${O}55` }}>
          <div>
            <div className="text-[10px] tracking-[0.32em] uppercase font-bold" style={{ color: WINE }}>
              ★ WHAT THIS IS · IN PLAIN ENGLISH
            </div>
            <h2 className="font-serif-display text-2xl md:text-4xl uppercase leading-tight mt-1"
                style={{ color: WINE, textWrap: "balance" }}>
              A Smarter Water Bottle.<br/>That&apos;s All.
            </h2>
            <p className="mt-3 text-base md:text-lg text-foreground/90 leading-relaxed">
              You drop tap water in. You hit a button. It turns the water into <strong>hydrogen-enriched water</strong> — the same stuff athletes, clinics, and pro-sports trainers have been paying $1,000+ machines for. This one fits in your hand and costs <strong>—</strong>.
            </p>
            <p className="mt-2 text-base md:text-lg text-foreground/90 leading-relaxed">
              <strong>Why I picked it for <Brand />:</strong> I drink mine every morning. My joints feel better. My head feels clearer. It&apos;s the one wellness gadget I actually use. So I asked Piurify if our members could earn on it — and they said yes.
            </p>
            <div className="mt-4 rounded-xl px-4 py-3 inline-flex items-start gap-2"
                 style={{ background: "#FFFFFF", border: `1.5px dashed ${O}88` }}>
              <Award className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: O }}/>
              <div className="text-sm md:text-base text-foreground/85 leading-relaxed">
                <strong style={{ color: WINE }}>The Faitharama promise:</strong> Everyone earns a flat <strong>50%</strong> on every Piurify they share. Flat rate. Forever. Same fair split as every other product on the platform.
              </div>
            </div>
            <div className="mt-5 flex flex-wrap items-center gap-2">
              <button onClick={buy("mike-intro")} data-testid="piurify-mike-intro-buy"
                      className="inline-flex items-center gap-1.5 px-5 py-3 rounded-md text-xs md:text-sm font-bold uppercase tracking-[0.18em] text-white shadow hover:scale-[1.03] transition"
                      style={{ background: G }}>
                Buy My Piurify · — <ExternalLink className="w-4 h-4"/>
              </button>
              <Link to="/business/affiliate-marketing-101"
                    data-testid="piurify-mike-intro-affiliate"
                    className="inline-flex items-center gap-1.5 px-5 py-3 rounded-md text-xs md:text-sm font-bold uppercase tracking-[0.18em] transition hover:scale-[1.03]"
                    style={{ background: "#FFFFFF", color: WINE, border: `1.5px solid ${WINE}` }}>
                <Sparkles className="w-4 h-4"/> Learn How To Sell It Too
              </Link>
            </div>
          </div>
          <div className="hidden md:block">
            <div className="rounded-xl overflow-hidden shadow-md"
                 style={{ border: `2px solid ${O}` }}>
              <img src={BOTTLE_ROSE_HERO} alt="Mike's Piurify bottle" className="block w-full h-auto"/>
            </div>
            <div className="mt-2 text-center text-[11px] italic text-foreground/65">
              Mike&apos;s personal pick.
            </div>
          </div>
        </div>
      </section>

      {/* ═══ VIDEO SECTION REMOVED PER MIKE'S DIRECT REQUEST ═══
           The "H2 Water Expert TYWO" video previewed a Black male presenter
           that displayed blurry and off-brand for Mike's senior Christian
           audience. Removed entirely. Page now flows straight from hero →
           features. If a clean lifestyle review video becomes available,
           it can slot back in here. */}

      {/* ═══ FEATURES ═══ */}
      <section id="features" className="max-w-6xl mx-auto px-5 md:px-8 pb-12">
        <div className="text-[10px] tracking-[0.32em] uppercase font-bold" style={{ color: O }}>
          ★ INNOVATION THAT POWERS YOUR HEALTH
        </div>
        <h2 className="font-serif-display text-3xl md:text-5xl uppercase leading-[1.05] mt-2"
            style={{ color: DARK, textWrap: "balance" }}>
          Four Things That Make Piurify Different.
        </h2>
        <div className="grid md:grid-cols-2 gap-5 mt-7" data-testid="piurify-features">
          {FEATURES.map((f) => (
            <div key={f.title} className="rounded-2xl bg-white border-2 p-6 md:p-7 shadow-md hover:shadow-xl transition"
                 style={{ borderColor: `${f.color}55` }}>
              <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
                   style={{ background: `${f.color}18`, color: f.color }}>
                <f.Icon className="w-6 h-6" />
              </div>
              <h3 className="font-serif-display text-2xl md:text-3xl uppercase leading-tight"
                  style={{ color: WINE, textWrap: "balance" }}>
                {f.title}
              </h3>
              <p className="text-foreground/80 mt-3 text-base md:text-lg leading-relaxed">
                {f.body}
              </p>
            </div>
          ))}
        </div>

        {/* Bottle gallery — all 4 colour variants */}
        <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="piurify-gallery">
          {[
            { src: BOTTLE_ROSE,   label: "PINK" },
            { src: BOTTLE_SILVER, label: "SILVER" },
            { src: BOTTLE_TEAL,   label: "TEAL" },
            { src: BOTTLE_BLACK,  label: "BLACK" },
          ].map((b) => (
            <div key={b.label} className="rounded-2xl bg-white border-2 overflow-hidden shadow-md hover:shadow-xl transition"
                 style={{ borderColor: `${O}40` }}>
              <img src={b.src} alt={`Piurify — ${b.label}`} className="block w-full h-auto" />
              <div className="px-4 py-2.5 text-[11px] tracking-[0.28em] uppercase font-bold text-center"
                   style={{ background: DARK, color: O }}>★ {b.label} ★</div>
            </div>
          ))}
        </div>

        {/* Innovation That Powers Your Health — clean responsive feature
            grid (replaces the old marketing infographic PNG which was
            overflowing and cropping the title on the right). */}
        <div className="mt-12 rounded-2xl p-6 md:p-10 shadow-md"
             style={{ background: "linear-gradient(180deg, #E8F2F5 0%, #D6E7EE 100%)", border: `2px solid ${O}40` }}
             data-testid="piurify-feature-graphic">
          <div className="text-center mb-7">
            <div className="inline-block px-4 py-1.5 rounded-full text-[10px] tracking-[0.32em] uppercase font-bold text-white shadow"
                 style={{ background: "#287F8E" }}>
              ★ HEALTH UPGRADE
            </div>
            <h3 className="font-serif-display text-3xl md:text-4xl lg:text-5xl uppercase mt-3 leading-[1.05]"
                style={{ color: "#1A2D4E", textWrap: "balance" }}>
              Innovation That Powers Your Health
            </h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
            {FEATURES.map((f) => (
              <div key={f.title} className="rounded-xl bg-white p-5 md:p-6 shadow-sm flex gap-4"
                   data-testid={`piurify-feature-${f.title.toLowerCase().replace(/\s+/g, '-')}`}>
                <div className="w-12 h-12 md:w-14 md:h-14 shrink-0 rounded-lg flex items-center justify-center text-white shadow"
                     style={{ background: f.color }}>
                  <f.Icon className="w-6 h-6 md:w-7 md:h-7"/>
                </div>
                <div className="min-w-0">
                  <div className="font-serif-display text-xl md:text-2xl leading-tight"
                       style={{ color: "#1A2D4E", fontWeight: 700 }}>
                    {f.title}
                  </div>
                  <p className="text-base md:text-lg mt-1.5 leading-relaxed" style={{ color: "#2C3D5C" }}>
                    {f.body}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* "Gain Control Over Your Health" — 6 health benefits graphic */}
        <div className="mt-6 rounded-2xl bg-white border-2 overflow-hidden shadow-md"
             style={{ borderColor: `${O}40` }} data-testid="piurify-benefits-graphic">
          <img src={BENEFITS_GRAPHIC} alt="Gain Control Over Your Health — 6 Piurify benefits" className="block w-full h-auto" />
        </div>

        {/* "How to Use Your Hydrogenator® Bottle" — 3-step usage guide */}
        <div className="mt-6 rounded-2xl overflow-hidden shadow-md border-2"
             style={{ borderColor: `${O}40` }} data-testid="piurify-howto-graphic">
          <img src={HOWTO_GRAPHIC} alt="How to Use Your Hydrogenator® Bottle — 3 simple steps" className="block w-full h-auto" />
        </div>
      </section>

      {/* ═══ HYDROGENATOR® FLASK — the bigger brother ═══ */}
      <section className="max-w-6xl mx-auto px-5 md:px-8 pb-14" data-testid="piurify-tabletop">
        <div className="rounded-3xl overflow-hidden shadow-lg"
             style={{ background: "linear-gradient(180deg, #FFFFFF 0%, #F4F7FB 60%, #E2ECF7 100%)", border: `4px solid ${O}` }}>
          <div className="grid md:grid-cols-[1fr_360px] items-center gap-6 p-7 md:p-10">
            <div>
              <div className="flex flex-wrap items-center gap-2 mb-4">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[10px] tracking-[0.28em] uppercase font-bold text-white"
                     style={{ background: "#287F8E" }} data-testid="piurify-tabletop-eyebrow">
                  ★ MEET THE FLASK · OUR MOST POWERFUL
                </div>
                {/* honest SOLD OUT pill so visitors aren't disappointed */}
                <span className="px-3 py-1.5 rounded-full text-[10px] tracking-[0.28em] uppercase font-bold text-white"
                      style={{ background: WINE }} data-testid="piurify-tabletop-soldout">
                  CURRENTLY SOLD OUT
                </span>
              </div>
              <h2 className="font-serif-display text-3xl sm:text-4xl md:text-5xl uppercase leading-[1.05]"
                  style={{ color: WINE, textWrap: "balance" }}>
                Hydrogenator&reg; Flask.
              </h2>
              <div className="text-base md:text-lg font-bold mt-2 uppercase tracking-[0.18em]" style={{ color: O }}>
                Our Most Powerful Hydrogen Experience
              </div>
              <p className="mt-4 text-base md:text-lg leading-relaxed max-w-xl" style={{ color: "#0F172A" }}>
                Engineered for maximum hydrogen concentration, longer battery performance, and premium BPA-free materials. Designed in-house for those who expect the best.
              </p>

              {/* 5.0 stars · 1 review (matches Piurify's product page) */}
              <div className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full border-2 bg-white"
                   style={{ borderColor: `${O}55` }} data-testid="piurify-tabletop-rating">
                {[0,1,2,3,4].map((i) => (
                  <Star key={i} className="w-4 h-4" fill={O} style={{ color: O }} />
                ))}
                <span className="text-sm font-bold ml-1" style={{ color: WINE }}>5.0 (1 review)</span>
              </div>

              {/* Pricing strip */}
              <div className="mt-6 flex flex-wrap items-baseline gap-3" data-testid="piurify-tabletop-price">
                <div className="font-serif-display text-5xl md:text-6xl font-bold" style={{ color: WINE }}>$ —</div>
                <div className="text-2xl line-through" style={{ color: "#9B8A6B" }}>$ —</div>
                <span className="px-3 py-1.5 rounded-full text-[11px] tracking-[0.22em] uppercase font-bold text-white"
                      style={{ background: G }}>Save —</span>
              </div>

              {/* Color picker */}
              <div className="mt-7" data-testid="piurify-tabletop-color-picker">
                <div className="text-[11px] tracking-[0.28em] uppercase font-bold mb-2" style={{ color: WINE }}>
                  Color: <span style={{ color: O }}>{tableColor.label}</span>
                </div>
                <div className="flex items-center gap-3">
                  {TABLETOP_COLORS.map((c) => {
                    const active = c.key === tableColor.key;
                    return (
                      <button
                        key={c.key}
                        type="button"
                        onClick={() => setTableColor(c)}
                        data-testid={`piurify-tabletop-swatch-${c.key}`}
                        aria-label={`Choose ${c.label}`}
                        aria-pressed={active}
                        className="w-12 h-14 rounded-md transition-all hover:scale-110 focus:outline-none"
                        style={{
                          background: c.hex,
                          border: active ? `3px solid #FFFFFF` : `2px solid ${WINE}33`,
                          boxShadow: active ? `0 0 0 3px ${O}, 0 4px 14px rgba(0,0,0,0.2)` : `0 2px 6px rgba(0,0,0,0.15)`,
                        }}
                      />
                    );
                  })}
                </div>
              </div>

              <div className="mt-7 flex flex-wrap items-center gap-3">
                <button onClick={buy("flask-cta")} data-testid="piurify-tabletop-buy"
                        className="inline-flex items-center gap-2 px-7 py-4 rounded-md text-sm font-bold uppercase tracking-[0.18em] text-white shadow-lg hover:scale-[1.03] transition"
                        style={{ background: G }}>
                  Check Availability On Piurify <ExternalLink className="w-4 h-4" />
                </button>
                <span className="text-[11px] tracking-[0.22em] uppercase font-bold" style={{ color: WINE }}>
                  Free Shipping · 60-Day Returns · Lifetime Warranty
                </span>
              </div>
            </div>

            {/* Hero device photo */}
            <div className="mx-auto" data-testid="piurify-tabletop-hero">
              <div className="rounded-3xl bg-white p-5 shadow-2xl transition-all"
                   style={{ border: `4px solid ${O}` }}>
                <img
                  src={tableColor.img}
                  alt={`Piurify Hydrogenator® Flask — ${tableColor.label}`}
                  className="block w-full h-auto transition-opacity duration-200"
                  style={{ maxWidth: 320 }}
                  key={tableColor.key}
                  data-testid="piurify-tabletop-hero-img"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Tabletop's own infographics */}
        <div className="mt-6 grid md:grid-cols-2 gap-6" data-testid="piurify-tabletop-infographics">
          <div className="rounded-2xl overflow-hidden shadow-md border-2"
               style={{ borderColor: `${O}40` }}>
            <img src={PPB_GRAPHIC}
                 alt="Market-Leading 8,630 PPB Hydrogen Concentration · SPE/PEM Technology"
                 className="block w-full h-auto" />
          </div>
          <div className="rounded-2xl overflow-hidden shadow-md border-2"
               style={{ borderColor: `${O}40` }}>
            <img src={STUDIES_GRAPHIC}
                 alt="1,800+ Studies on Molecular Hydrogen · 50+ Benefits"
                 className="block w-full h-auto" />
          </div>
        </div>

        {/* Flask walk-through video — compact width, same as customer-review */}
        <div className="mt-8" data-testid="piurify-flask-video">
          <div className="text-[10px] tracking-[0.32em] uppercase font-bold text-center" style={{ color: O }}>
            ★ FLASK WALK-THROUGH ★
          </div>
          <h3 className="font-serif-display text-2xl md:text-3xl uppercase leading-tight mt-2 text-center"
              style={{ color: WINE, textWrap: "balance" }}>
            See The Flask In Action.
          </h3>
          <div className="mt-5 mx-auto rounded-2xl overflow-hidden shadow-2xl bg-black"
               style={{ border: `4px solid ${O}`, maxWidth: 360 }}>
            <video
              controls
              preload="metadata"
              playsInline
              poster={TABLETOP_BLACK}
              className="block w-full h-auto"
              data-testid="piurify-flask-video-element"
            >
              <source src={FLASK_VIDEO_MP4} type="video/mp4" />
              Your browser doesn&apos;t support embedded video. {" "}
              <a href={FLASK_VIDEO_MP4} target="_blank" rel="noopener noreferrer" style={{ color: O }}>
                Download the video here
              </a>.
            </video>
          </div>
        </div>
      </section>

      {/* ═══ ADVANCED WATER SOLUTIONS — the real Piurify lineup ═══ */}
      <section className="max-w-6xl mx-auto px-5 md:px-8 pb-14" data-testid="piurify-favorites">
        <div className="text-center mb-8">
          <div className="text-[10px] tracking-[0.32em] uppercase font-bold" style={{ color: O }}>
            ★ THE WHOLE PIURIFY FAMILY ★
          </div>
          <h2 className="font-serif-display text-3xl md:text-5xl uppercase mt-2 leading-[1.05]"
              style={{ color: DARK, textWrap: "balance" }}>
            Advanced Water Solutions<br/>For Optimal Health.
          </h2>
          <p className="mt-3 max-w-2xl mx-auto text-base md:text-lg text-foreground/75 leading-relaxed">
            Bottles. Pitchers. Flask. Everything Piurify makes is on sale right now. <strong>Tap any card — every order through this page still pays Mike his 10%.</strong>
          </p>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="piurify-favorites-grid">
          {FAVORITES.map((f) => (
            <button
              type="button"
              key={f.key}
              onClick={buy(`fav-${f.key}`)}
              data-testid={`piurify-fav-${f.key}`}
              className="text-left rounded-2xl bg-white border-2 p-5 shadow-md hover:shadow-2xl hover:-translate-y-1 transition-all flex flex-col"
              style={{ borderColor: `${O}40` }}
            >
              <div className="flex items-center justify-between gap-2 mb-4">
                <div className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full border-2 text-[11px] font-bold"
                     style={{ borderColor: `${WINE}25`, color: DARK, background: "#FFF" }}>
                  <Star className="w-3 h-3" fill={O} style={{ color: O }} />
                  <span>{f.stars.toFixed(1)} ({f.reviews})</span>
                </div>
                {f.badge && (
                  <span className="px-2.5 py-1 rounded-full text-[10px] tracking-[0.18em] uppercase font-bold text-white"
                        style={{ background: `linear-gradient(135deg, ${f.badgeColor}, ${f.badgeColor}CC)` }}>
                    {f.badge}
                  </span>
                )}
              </div>
              <h3 className="font-serif-display text-lg md:text-xl leading-tight uppercase"
                  style={{ color: WINE, textWrap: "balance" }}>
                {f.name}
              </h3>
              <p className="text-sm md:text-base text-foreground/75 mt-2 leading-snug flex-1">
                {f.desc}
              </p>
              {/* Pricing row */}
              {f.price && (
                <div className="mt-3 flex items-baseline gap-2 flex-wrap" data-testid={`piurify-fav-price-${f.key}`}>
                  <span className="font-serif-display text-xl font-bold" style={{ color: WINE }}>{f.price}</span>
                  {f.wasPrice && (
                    <span className="text-sm line-through" style={{ color: "#9B8A6B" }}>{f.wasPrice}</span>
                  )}
                </div>
              )}
              <div className="mt-3 inline-flex items-center gap-1 text-[11px] tracking-[0.18em] uppercase font-bold"
                   style={{ color: f.soldOut ? WINE : O }}>
                {f.soldOut ? "Check Availability" : "Shop On Piurify"} <ExternalLink className="w-3.5 h-3.5" />
              </div>
            </button>
          ))}
        </div>

        {/* Lineup photo from Piurify */}
        <div className="mt-10 rounded-2xl bg-white border-2 overflow-hidden shadow-md"
             style={{ borderColor: `${O}40` }} data-testid="piurify-family-lineup">
          <img src={FAMILY_LINEUP} alt="Piurify product family lineup" className="block w-full h-auto" />
        </div>
      </section>

      {/* ═══ FINAL CTA ═══ */}
      <section className="max-w-5xl mx-auto px-5 md:px-8 pb-16">
        <div className="rounded-2xl p-7 md:p-9 text-center"
             style={{ background: `linear-gradient(135deg, #0F1A26 0%, #1F4068 100%)`, border: `3px solid ${O}` }}>
          <div className="text-[10px] tracking-[0.32em] uppercase font-bold" style={{ color: O }}>
            ★ READY TO TRY IT? ★
          </div>
          <h3 className="font-serif-display text-3xl md:text-5xl uppercase mt-2 leading-tight" style={{ color: "#FFFFFF" }}>
            Order Your Piurify Bottle.
          </h3>
          <p className="mt-3 max-w-2xl mx-auto text-base md:text-lg leading-relaxed text-white/85">
            Drink hydrogen water for 60 days. Notice the difference. If you don't love it — send it back. Lifetime warranty either way.
          </p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
            <button onClick={buy("footer")} data-testid="piurify-buy-footer"
                    className="inline-flex items-center gap-2 px-7 py-4 rounded-md text-base font-bold uppercase tracking-[0.18em] text-white shadow-lg hover:scale-[1.03] transition"
                    style={{ background: G }}>
              Get It For $ — <ExternalLink className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Become an affiliate */}
        <div className="mt-8 rounded-2xl border-2 p-6 md:p-7 bg-white"
             style={{ borderColor: `${O}55` }} data-testid="piurify-affiliate-signup">
          <div className="flex items-start gap-4 flex-wrap md:flex-nowrap">
            <div className="w-14 h-14 rounded-xl flex items-center justify-center shrink-0"
                 style={{ background: `${O}18`, color: WINE }}>
              <Award className="w-7 h-7" />
            </div>
            <div className="flex-1 min-w-[260px]">
              <div className="text-[10px] tracking-[0.28em] uppercase font-bold" style={{ color: O }}>
                ★ WANT TO SELL IT TOO?
              </div>
              <h3 className="font-serif-display text-2xl md:text-3xl uppercase mt-1 leading-tight" style={{ color: WINE }}>
                Become A Piurify Affiliate.
              </h3>
              <p className="text-foreground/80 mt-2 text-base leading-relaxed">
                If you've got a wellness audience, an email list, or you just love telling people about good products — sign up for Piurify's affiliate programme through UpPromote and earn a commission on every bottle you refer. Free to join. Pays direct.
              </p>
            </div>
            <a href={PARTNER_SIGNUP_URL} target="_blank" rel="noopener noreferrer"
               data-testid="piurify-affiliate-cta"
               className="inline-flex items-center gap-2 px-6 py-3 rounded-md text-xs font-bold uppercase tracking-[0.18em] text-white shadow hover:scale-[1.03] transition shrink-0"
               style={{ background: WINE }}>
              Sign Up As An Affiliate <ExternalLink className="w-4 h-4" />
            </a>
          </div>
        </div>

        {/* Disclosure */}
        <p className="mt-7 text-center text-[11px] italic text-foreground/55 px-3" style={{ textWrap: "balance" }}>
          Mike earns a small commission (about 10%) on every Piurify bottle ordered through this page. Your price doesn't change — Piurify pays Mike directly. Thank you for supporting <Brand /> with every order.
        </p>
      </section>

      <SiteFooter />
    </div>
  );
}
